import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, FileText, ChevronDown } from 'lucide-react';
export function HeroSection() {
  const [dossierOpen, setDossierOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDossierOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <section id="hero" className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden" data-id="element-210">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-211">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-212">
          {/* Left Visual - Photo First */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="relative flex items-center justify-center" data-id="element-213">
            {/* Photo container with luxurious shining gold frame */}
            <div className="relative w-[280px] h-[340px] md:w-[320px] md:h-[400px]" data-id="element-214">
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-[#C9A84C]/20 via-[#F5E6A3]/10 to-[#B8963E]/20 blur-md" data-id="element-215"></div>
              <div className="absolute inset-0 rounded-xl gold-frame p-[5px]" data-id="element-216">
                <div className="absolute inset-0 rounded-xl gold-frame-shimmer pointer-events-none z-10 mix-blend-overlay" data-id="element-217"></div>
                <div className="w-full h-full rounded-[8px] bg-gradient-to-br from-[#6B5210] via-[#3D2E08] to-[#6B5210] p-[2px]" data-id="element-218">
                  <div className="w-full h-full rounded-[7px] bg-gradient-to-br from-[#D4B86A] via-[#C9A84C] to-[#D4B86A] p-[3px]" data-id="element-219">
                    <div className="w-full h-full rounded-[5px] overflow-hidden relative" data-id="element-220">
                      <img src="https://cdn.magicpatterns.com/uploads/jVRAR7D2KgxJELrSqGCMyL/847555eb-79d4-4f40-a577-bc32d0f5c572.png" alt="Peter Sheldon - Senior Program Manager" className="w-full h-full object-cover object-top" data-id="element-221" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B]/20 via-transparent to-transparent" data-id="element-222"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -top-3 -left-3 w-7 h-7 corner-sparkle" data-id="element-223">
                <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-224">
                  <path d="M14 0L16.5 11.5L28 14L16.5 16.5L14 28L11.5 16.5L0 14L11.5 11.5L14 0Z" fill="#D4B86A" data-id="element-225" />
                </svg>
              </div>
              <div className="absolute -top-3 -right-3 w-7 h-7 corner-sparkle-delay" data-id="element-226">
                <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-227">
                  <path d="M14 0L16.5 11.5L28 14L16.5 16.5L14 28L11.5 16.5L0 14L11.5 11.5L14 0Z" fill="#D4B86A" data-id="element-228" />
                </svg>
              </div>
              <div className="absolute -bottom-3 -left-3 w-7 h-7 corner-sparkle-delay" data-id="element-229">
                <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-230">
                  <path d="M14 0L16.5 11.5L28 14L16.5 16.5L14 28L11.5 16.5L0 14L11.5 11.5L14 0Z" fill="#D4B86A" data-id="element-231" />
                </svg>
              </div>
              <div className="absolute -bottom-3 -right-3 w-7 h-7 corner-sparkle" data-id="element-232">
                <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-233">
                  <path d="M14 0L16.5 11.5L28 14L16.5 16.5L14 28L11.5 16.5L0 14L11.5 11.5L14 0Z" fill="#D4B86A" data-id="element-234" />
                </svg>
              </div>

              <div className="absolute top-1/2 -left-2 w-4 h-4 -translate-y-1/2 corner-sparkle" data-id="element-235">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-236">
                  <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z" fill="#F5E6A3" data-id="element-237" />
                </svg>
              </div>
              <div className="absolute top-1/2 -right-2 w-4 h-4 -translate-y-1/2 corner-sparkle-delay" data-id="element-238">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-239">
                  <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z" fill="#F5E6A3" data-id="element-240" />
                </svg>
              </div>
            </div>

            <motion.div initial={{
            y: 20,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} className="absolute -bottom-8 -right-4 lg:-right-8 bg-[#FDFBF7] p-5 rounded-lg shadow-xl border-l-4 border-corporate-teal hidden md:block" data-id="element-241">
              <p className="text-3xl font-bold text-corporate-blue mb-1" data-id="element-242">20+</p>
              <p className="text-sm text-[#7A7062] font-medium uppercase tracking-wide" data-id="element-243">
                Years of Excellence
              </p>
            </motion.div>
          </motion.div>

          {/* Right Content - Text Second */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} data-id="element-244">
            <h2 className="text-corporate-teal font-semibold text-base mb-2 tracking-wide uppercase" data-id="element-245">
              Senior Program Manager
            </h2>
            <h1 className="text-4xl lg:text-6xl font-bold text-corporate-blue mb-5 leading-tight" data-id="element-246">
              Driving Strategic <br data-id="element-247" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-corporate-blue to-corporate-teal" data-id="element-248">
                Transformation
              </span>
            </h1>
            <p className="text-lg text-[#6B6356] mb-5 max-w-lg leading-relaxed" data-id="element-249">
              Delivering high-impact initiatives for global enterprises.
              Specializing in operational excellence, stakeholder alignment, and
              large-scale digital transformation.
            </p>

            <div className="space-y-4 mb-7 max-w-lg" data-id="element-250">
              <p className="text-sm text-[#6B6356] leading-relaxed" data-id="element-251">
                I work with organizations executing complex, high-value programs
                that demand precision, visibility, and disciplined project
                control. My focus is helping engineering, construction, energy,
                and enterprise teams deliver major initiatives on time, within
                budget, and with full operational alignment.
              </p>
              <p className="text-sm text-[#6B6356] leading-relaxed" data-id="element-252">
                With more than 20 years of leadership in program management,
                project controls, and schedule engineering, I specialize in
                building integrated scheduling and control systems that improve
                decision-making, strengthen cross-functional coordination, and
                reduce execution risk. My work spans large-scale infrastructure,
                energy, aerospace, and enterprise environments where
                performance, compliance, and cost discipline are critical.
              </p>
              <p className="text-sm text-[#6B6356] leading-relaxed" data-id="element-253">
                My unique advantage is the ability to combine deep technical
                scheduling expertise with executive-level program leadership. I
                design systems that give organizations clear visibility into
                performance, forecast risk early, and enable teams to execute
                complex programs with confidence, efficiency, and measurable
                results.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-254">
              <button className="px-7 py-3.5 bg-corporate-blue text-white font-medium rounded hover:bg-opacity-90 transition-all flex items-center justify-center gap-2 group text-sm" data-id="element-255">
                View Experience
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-256" />
              </button>
              <a href="https://heyzine.com/flip-book/779702ad6e.html" target="_blank" rel="noopener noreferrer" className="px-7 py-3.5 border border-[#D4CBBC] text-[#4A4438] font-medium rounded hover:border-corporate-blue hover:text-corporate-blue transition-all flex items-center justify-center gap-2 text-sm" data-id="element-257">
                View Resume
                <Download size={16} data-id="element-258" />
              </a>

              {/* Executive Dossier Dropdown */}
              <div className="relative" ref={dropdownRef} data-id="element-259">
                <button onClick={() => setDossierOpen(!dossierOpen)} className="px-7 py-3.5 bg-corporate-teal text-white font-medium rounded hover:bg-opacity-90 transition-all flex items-center justify-center gap-2 text-sm w-full" data-id="element-260">
                  <FileText size={16} data-id="element-261" />
                  Executive Dossier
                  <ChevronDown size={14} className={`transition-transform duration-200 ${dossierOpen ? 'rotate-180' : ''}`} data-id="element-262" />
                </button>

                {dossierOpen && <motion.div initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.15
              }} className="absolute top-full left-0 right-0 mt-1.5 bg-[#FDFBF7] border border-[#E8E0D0] rounded-lg shadow-lg overflow-hidden z-50" data-id="element-263">
                    <a href="https://heyzine.com/flip-book/2f453e47c1.html" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-4 py-3 text-sm text-[#4A4438] hover:bg-corporate-teal/10 hover:text-corporate-blue transition-colors border-b border-[#E8E0D0]/60" data-id="element-264">
                      <FileText size={15} className="text-corporate-teal" data-id="element-265" />
                      Executive Bio
                    </a>
                    <a href="https://heyzine.com/flip-book/32de8574e8.html" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-4 py-3 text-sm text-[#4A4438] hover:bg-corporate-teal/10 hover:text-corporate-blue transition-colors border-b border-[#E8E0D0]/60" data-id="element-266">
                      <FileText size={15} className="text-corporate-teal" data-id="element-267" />
                      Executive SWOT Assessment
                    </a>
                    <a href="https://heyzine.com/flip-book/779702ad6e.html" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-4 py-3 text-sm text-[#4A4438] hover:bg-corporate-teal/10 hover:text-corporate-blue transition-colors" data-id="element-268">
                      <FileText size={15} className="text-corporate-teal" data-id="element-269" />
                      Executive Dossier
                    </a>
                  </motion.div>}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}